import { createMiddleware } from "@tanstack/solid-start";
import { getRequest } from "@tanstack/solid-start/server";
import { getRequiredSession } from "~/server/session";

/**
 * Request middleware that gates all routes on authentication.
 * Uses type:'request' so the server callback receives the raw Request — headers
 * are read server-side, never trusted from client input.
 *
 * On success it injects { user } into context so handlers never re-fetch the
 * session and never accept a userId from the caller.
 *
 * Usage:
 *   server: { middleware: [authMiddleware], handlers: { GET: async ({ context }) => context.user } }
 *
 * For createServerFn usage the same middleware composes identically:
 *   createServerFn().middleware([authMiddleware]).handler(async ({ context }) => context.user)
 */
export const authMiddleware = createMiddleware({ type: "request" }).server(
	async ({ request, next }) => {
		// getRequiredSession throws a 401 Response when unauthenticated —
		// user is always derived from server-verified session, never from input.
		const session = await getRequiredSession(request.headers);

		return next({
			context: {
				user: session.user,
			},
		});
	},
);

export const authFunctionMiddleware = createMiddleware({
	type: "function",
}).server(async ({ next }) => {
	const request = getRequest();
	const session = await getRequiredSession(request.headers);

	return next({
		context: {
			user: session.user,
			session,
		},
	});
});
